import cx from 'classnames';
import debounce from 'lodash/debounce';
import { memo, useContext, useRef } from 'react';

import { isInGame, isWCFRequired } from '~/constants';
import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import useMount from '~/hooks/useMount';
import { RefKeys, refManager } from '~/managers/refManager';
import { useAppSelector } from '~/store';
import { isDialogOpenSelector } from '~/store/selectors/dialogsSelectors';
import {
  supplyisMapZoomedSelector,
  supplyIsSidebarOpenedSelector,
  supplySelectedBuildingIdSelector,
} from '~/store/selectors/supplySelectors';
import { ENTRY_SOURCE } from '~/store/slices/settingsSlice';

import { IsFromSearchContext } from '~/Providers/IsFromSearch';
import ClanRatings from '~/Components/ClanRatings/ClanRatings';
import ClanSupply from '~/Components/ClanSupply/ClanSupply';
import ClanHeader from '~/UIKit/ClanHeader/ClanHeader';
import ClanWallet from '~/UIKit/ClanWallet/ClanWallet';
import ClansInformerButton from '~/UIKit/ClansInformerButton/ClansInformerButton';

import styles from './ClanProfile.module.scss';
import { useWcfMapScrollSync } from './useWcfMapScrollSync';

import type { ClanInfoType } from '~/Actions/ActionClanProfile';

interface ClanProfileContentProps {
  clan: ClanInfoType;
  isOwnClanBase: boolean;
}

const ClanProfileContent = ({ clan, isOwnClanBase = false }: ClanProfileContentProps) => {
  const isFromSearch = useContext(IsFromSearchContext);

  const hasOpenedDialogs = useAppSelector(isDialogOpenSelector);
  const isMapZoomed = useAppSelector(supplyisMapZoomedSelector);
  const isSidebarOpened = useAppSelector(supplyIsSidebarOpenedSelector);
  const selectedBuildingId = useAppSelector(supplySelectedBuildingIdSelector);

  const appRef = refManager.getRef(RefKeys.APP);
  const mapRef = useRef<HTMLDivElement>(null);

  const clanId = clan.id;
  const accumulativeResource = clan?.accumulativeResource || 0;
  const isHeaderFooterHidden = selectedBuildingId || isMapZoomed;

  useMount(() => {
    if (isOwnClanBase && !isFromSearch) {
      const debouncedSendEvents = debounce(() => {
        dwhExport.push(DWH_EVENTS.SUPPLY.OPEN_PAGE);
      }, 1000);

      debouncedSendEvents();

      return () => {
        debouncedSendEvents.cancel();
      };
    }
  });

  useWcfMapScrollSync({
    isEnabled: isWCFRequired,
    isBlocked: hasOpenedDialogs,
    appRef: appRef,
    mapRef,
  });

  return (
    <>
      <div className={styles.supply} ref={mapRef}>
        <ClanSupply clan={clan} isOwnClanBase={isOwnClanBase} />
      </div>

      <ClanHeader
        className={cx(styles.profileHead, { [styles.isHidden]: isHeaderFooterHidden })}
        clan={clan}
        isOwnClanBase={isOwnClanBase}
      />

      <div
        className={cx(styles.profileRatings, {
          [styles.isHidden]: isHeaderFooterHidden || isSidebarOpened,
        })}
      >
        <ClanRatings clanId={clanId} isOwnClanBase={isOwnClanBase} />
      </div>

      {isOwnClanBase ? (
        <>
          <ClansInformerButton clanId={clanId} source={ENTRY_SOURCE.PROFILE} />
          <div
            className={cx(styles.clanWallet, {
              [styles.inGame]: isInGame,
            })}
          >
            <ClanWallet clanResource={clan.personalResource} accumulativeResource={accumulativeResource} />
          </div>
        </>
      ) : null}
    </>
  );
};

export default memo(ClanProfileContent);
